import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../../utils/soundManager';
import './VideoShowcase.css';

const VideoShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const facts = [
    'Each movement takes 400+ hours to complete',
    'Over 200 precision components',
    'Hand-assembled by a single master',
    'Accuracy within ±1 second per year',
    '15 quality control checkpoints'
  ];

  const [currentFact, setCurrentFact] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Rotate facts every 5 seconds when video is not playing
  useEffect(() => {
    if (!isPlaying) {
      const interval = setInterval(() => {
        setCurrentFact((prev) => (prev + 1) % facts.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [isPlaying, facts.length]);

  // Hide controls after 3 seconds of inactivity when playing
  useEffect(() => {
    if (isPlaying && hasInteracted) {
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [isPlaying, hasInteracted]);

  const handlePlayPause = () => {
    soundManager.play('ui-click', 0.4);
    
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().catch(err => {
          console.log('Video play failed:', err);
        });
        setIsPlaying(true);
        setHasInteracted(true);
      }
    }
  };

  const handleMouseMove = () => {
    if (isPlaying) {
      setShowControls(true);
      
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  return (
    <section 
      id="video-showcase" 
      ref={sectionRef}
      className={`video-showcase-section ${isVisible ? 'visible' : ''}`}
    >
      <div className="video-showcase-container">
        {/* Section Header */}
        <div className="video-header">
          <div className="header-label">
            <span className="label-line"></span>
            <span className="label-text">The Making Of</span>
            <span className="label-line"></span>
          </div>
          <h2 className="video-title">Handcrafted Perfection</h2>
          <p className="video-subtitle">
            Witness the artistry and precision that goes into every CHRONOS timepiece. 
            A journey through our Geneva manufacture.
          </p>
        </div>

        {/* Video Container */}
        <div 
          className="video-player-container"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => isPlaying && setShowControls(false)}
        >
          {/* Video Element */}
          <video
            ref={videoRef}
            className="showcase-video"
            loop={false}
            playsInline
            onEnded={handleVideoEnd}
            poster="/images/craftsmanship/watchmaker-hands.jpg"
          >
            <source src="/videos/craftsmanship.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Video Overlay (when not playing) */}
          {!isPlaying && (
            <div className="video-overlay">
              <div className="overlay-gradient"></div>
              
              {/* Ambient Facts */}
              <div className="video-facts">
                <div className="fact-container">
                  {facts.map((fact, index) => (
                    <div
                      key={index}
                      className={`fact-item ${currentFact === index ? 'active' : ''}`}
                    >
                      <span className="fact-icon">◆</span>
                      <span className="fact-text">{fact}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Play Button */}
          <div 
            className={`play-button-container ${showControls ? 'visible' : ''} ${isPlaying ? 'playing' : ''}`}
            onClick={handlePlayPause}
          >
            <div className="play-button">
              {!isPlaying ? (
                <svg className="play-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M8 5v14l11-7z" fill="currentColor"/>
                </svg>
              ) : (
                <svg className="pause-icon" viewBox="0 0 24 24" fill="none">
                  <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                  <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                </svg>
              )}
            </div>
            {!isPlaying && (
              <span className="play-text">Watch the Story</span>
            )}
          </div>

          {/* Video Controls Bar */}
          {isPlaying && (
            <div className={`video-controls-bar ${showControls ? 'visible' : ''}`}>
              <button 
                className="control-btn"
                onClick={handlePlayPause}
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                  <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                </svg>
              </button>
              
              <div className="control-info">
                <span className="control-text">The Craftsmanship Journey</span>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Info Cards */}
        <div className="video-info-cards">
          <div className="info-card">
            <span className="info-icon">⚙</span>
            <h3 className="info-title">Swiss Movement</h3>
            <p className="info-description">
              Each movement is hand-assembled and regulated by master watchmakers 
              with decades of experience.
            </p>
          </div>

          <div className="info-card">
            <span className="info-icon">◆</span>
            <h3 className="info-title">Precious Materials</h3>
            <p className="info-description">
              Only the finest materials: 18K gold, sapphire crystal, 
              and ethically-sourced alligator leather.
            </p>
          </div>

          <div className="info-card">
            <span className="info-icon">◈</span>
            <h3 className="info-title">Lifetime Service</h3>
            <p className="info-description">
              Every CHRONOS timepiece comes with complimentary lifetime 
              servicing at our Geneva manufacture.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="video-cta">
          <p className="cta-text">
            Experience the craftsmanship in person at our Geneva boutique
          </p>
          <button 
            className="cta-button"
            onClick={() => {
              soundManager.play('ui-click', 0.4);
              document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Schedule a Visit
          </button>
        </div>
      </div>
    </section>
  );
};

export default VideoShowcase;